import { Select, type ComboboxData, type SelectProps } from "@mantine/core";
import type { MRT_Cell, MRT_Row, MRT_RowData } from "mantine-react-table-open";
import { useEffect, useState } from "react";

type ValueType = string | null;

interface ControlledMantineEditSelectProps<
  T extends MRT_RowData
> extends SelectProps {
  cell: MRT_Cell<T, ValueType>;
  columnId: string;
  errorText: string | undefined;
  label?: string;
  overrideValue?: string;
  row: MRT_Row<T>;
  data: ComboboxData;
}

/** Custom controlled version of Mantine Select for mantine-react-table.
 * Inspired by this workaround: https://github.com/KevinVandy/mantine-react-table/discussions/8#discussioncomment-8759454
 */
function ControlledMantineEditSelect<T extends MRT_RowData>({
  cell,
  columnId,
  errorText,
  label,
  overrideValue,
  row,
  data,
  onChange,
  ...rest
}: ControlledMantineEditSelectProps<T>) {
  const [value, setValue] = useState<ValueType>(
    cell.getValue()?.toString() ?? null
  );

  useEffect(() => {
    //@ts-expect-error when using a generic type, the valuesCache is readonly
    row._valuesCache[columnId] = value;
  }, [value, columnId, row]);

  useEffect(() => {
    if (typeof overrideValue !== "undefined") {
      setValue(overrideValue);
    }
  }, [overrideValue]);

  function handleChange(
    newValue: ValueType,
    option: Parameters<NonNullable<SelectProps["onChange"]>>[1]
  ) {
    setValue(newValue);
    onChange?.(newValue, option);
  }

  return (
    <Select
      {...rest}
      aria-label={label}
      data={data}
      error={errorText}
      value={value}
      onChange={handleChange}
    />
  );
}

export { ControlledMantineEditSelect };
